import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface StagePreview {
  stage_id: string;
  display_name: string;
  wave_count: number;
  has_boss: boolean;
  stamina_cost: number | null;
  max_heroes: number;
}

interface BattleResultData {
  victory: boolean;
  stars: number;
  turns_used: number;
  heroes_alive: number;
  heroes_total: number;
  cost: { entries: { ref_key: string; amount: { value: number } }[] };
  reward: { entries: { package: string; ref_key: string; amount: { value: number } }[] };
  loot_rolls: string[];
  combat_log: { turn: number; actor: string; action: string; targets: string[]; damage: number }[];
  events: { type: string; entity_id?: string; stage_id?: string }[];
}

interface SimReport {
  iterations: number;
  win_rate: number;
  avg_turns_win: number;
  avg_stars: number;
  avg_hero_casualties: number;
}

export default function Combat({ session }: Props) {
  const [stages, setStages] = useState<StagePreview[]>([]);
  const [selected, setSelected] = useState('');
  const [heroJson, setHeroJson] = useState(JSON.stringify([{
    hero_id: "hero1", level: 10,
    stats: { hp: 500, attack: 200, defense: 50, speed: 100 },
    element: "fire", abilities: ["slash"]
  }], null, 2));
  const [result, setResult] = useState<BattleResultData | null>(null);
  const [simReport, setSimReport] = useState<SimReport | null>(null);
  const [simIter, setSimIter] = useState(100);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`/api/v1/games/${session.gameId}/modules/combat/stages`)
      .then(r => r.json())
      .then((d: { stages: StagePreview[] }) => {
        setStages(d.stages);
        if (d.stages.length > 0 && !selected) setSelected(d.stages[0].stage_id);
      })
      .catch(e => setError(e.message));
  }, [session.gameId]);

  const runBattle = async () => {
    if (!selected) return;
    setBusy(true); setError(null); setResult(null); setSimReport(null);
    try {
      const heroes = JSON.parse(heroJson);
      const res = await fetch(`/api/v1/games/${session.gameId}/modules/combat/battle/${selected}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ heroes, troops: [] }),
      });
      if (!res.ok) throw new Error(await res.text());
      setResult(await res.json());
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const runSim = async () => {
    if (!selected) return;
    setBusy(true); setError(null); setSimReport(null);
    try {
      const heroes = JSON.parse(heroJson);
      const res = await fetch(`/api/v1/games/${session.gameId}/modules/combat/simulate/${selected}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ heroes, iterations: simIter }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSimReport(await res.json());
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const current = stages.find(s => s.stage_id === selected);

  return (
    <Container>
      <PageHeader title="Combat" subtitle="Test battles, simulate for balance, and tune stage difficulty." />
      {error && <div className="error-banner">{error}</div>}

      <div className="card mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">Stage</label>
            <select className="input" value={selected} onChange={e => setSelected(e.target.value)}>
              {stages.length === 0 && <option value="">No stages published</option>}
              {stages.map(s => <option key={s.stage_id} value={s.stage_id}>{s.display_name} ({s.wave_count} waves{s.has_boss ? ', boss' : ''})</option>)}
            </select>
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={runBattle}>Battle</button>
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">Iterations</label>
              <input className="input" type="number" value={simIter} onChange={e => setSimIter(parseInt(e.target.value, 10) || 10)} style={{ width: 100 }} />
            </div>
            <button className="btn" disabled={busy} onClick={runSim}>Simulate</button>
          </div>
        </div>
        {current && (
          <div className="flex gap-4 mt-3 text-xs text-fg-dim">
            {current.stamina_cost && <span>Stamina: {current.stamina_cost}</span>}
            <span>Max heroes: {current.max_heroes}</span>
            <span>Waves: {current.wave_count}</span>
          </div>
        )}
      </div>

      <div className="card mb-5">
        <h3 className="text-sm font-semibold m-0 mb-3">Hero lineup (JSON)</h3>
        <textarea className="input font-mono" value={heroJson} onChange={e => setHeroJson(e.target.value)} style={{ minHeight: 120 }} />
      </div>

      {simReport && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 mb-5">
          {[
            { label: 'Win rate', value: `${(simReport.win_rate * 100).toFixed(1)}%` },
            { label: 'Avg turns', value: simReport.avg_turns_win.toFixed(1) },
            { label: 'Avg stars', value: simReport.avg_stars.toFixed(2) },
            { label: 'Avg casualties', value: simReport.avg_hero_casualties.toFixed(2) },
          ].map(s => (
            <div key={s.label} className="card">
              <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-2">{s.label}</div>
              <div className="text-[26px] font-semibold tabular-nums tracking-tight">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {result && (
        <>
          <div className="card mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold m-0">Battle result</h3>
              <div className="flex gap-2">
                <span className={`badge ${result.victory ? 'badge-published' : 'badge-draft'}`}>{result.victory ? 'Victory' : 'Defeat'}</span>
                {result.stars > 0 && <span className="badge badge-current">{result.stars} star{result.stars > 1 ? 's' : ''}</span>}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><span className="text-fg-dim">Turns:</span> {result.turns_used}</div>
              <div><span className="text-fg-dim">Heroes alive:</span> {result.heroes_alive}/{result.heroes_total}</div>
              <div><span className="text-fg-dim">Loot rolls:</span> {result.loot_rolls.join(', ') || 'none'}</div>
              <div><span className="text-fg-dim">Events:</span> {result.events.length}</div>
            </div>
          </div>

          <details className="mb-5">
            <summary className="text-xs text-fg-muted cursor-pointer hover:text-fg-dim">Combat log ({result.combat_log.length} entries)</summary>
            <div className="table-wrap mt-2">
              <table>
                <thead><tr><th>Turn</th><th>Actor</th><th>Action</th><th>Target</th><th>Damage</th></tr></thead>
                <tbody>
                  {result.combat_log.slice(0, 50).map((l, i) => (
                    <tr key={i}>
                      <td className="text-fg-muted">{l.turn}</td>
                      <td className="font-medium">{l.actor}</td>
                      <td>{l.action}</td>
                      <td>{l.targets.join(', ')}</td>
                      <td className="font-mono tabular-nums">{l.damage.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}

      {stages.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No stages published</div>
          <div className="text-xs text-fg-muted">Define stages, monsters, and abilities in Config Explorer → Combat</div>
        </div>
      )}
    </Container>
  );
}
